import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Table,
  Card,
  Space,
  Button,
  Input,
  Select,
  DatePicker,
  Tag,
  Drawer,
  Descriptions,
  Typography,
  message,
  Tooltip,
  Alert,
  Grid,
  List,
  Pagination,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import {
  createAuditRequestGate,
  isAuditRequestCanceled,
  shouldLoadAuditPageImmediately,
} from "./auditRequestGate";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { useBreakpoint } = Grid;

const ACTIONS_WITH_ENRICHED_DETAILS = new Set(["CREATE", "UPDATE", "DELETE"]);

const DETAIL_KEY_LABELS = {
  created_at: "Date de creation",
  updated_at: "Date de mise a jour",
  updated_fields: "Champs mis a jour",
  changed_fields: "Champs modifies",
  modified_fields: "Champs modifies",
  old_values: "Anciennes valeurs",
  new_values: "Nouvelles valeurs",
  old_value: "Ancienne valeur",
  new_value: "Nouvelle valeur",
  old_total_amount: "Ancien montant total",
  total_amount: "Nouveau montant total",
  payment_number: "No paiement",
  invoice_number: "No facture",
  bill_number: "No facture fournisseur",
  shipment_number: "No dossier",
  item_code: "Code article",
  client_name: "Client",
  supplier_name: "Fournisseur",
  order_id: "Commande",
  product_id: "Produit",
  company_name: "Entreprise",
  warehouse_name: "Établissement",
  payment_type: "Type de paiement",
  resource_id: "ID ressource",
};

const IDENTIFIER_KEYS = [
  "payment_number",
  "invoice_number",
  "bill_number",
  "shipment_number",
  "item_code",
  "name",
  "company_name",
  "client_name",
  "supplier_name",
  "email",
  "order_id",
  "product_id",
];

const STATUS_CONFIG = {
  success: { icon: <CheckCircleOutlined />, color: "success", label: "Succes" },
  failure: { icon: <CloseCircleOutlined />, color: "error", label: "Echec" },
  partial: { icon: <WarningOutlined />, color: "warning", label: "Partiel" },
};

const METHOD_COLORS = {
  GET: "default",
  POST: "green",
  PUT: "orange",
  DELETE: "red",
  PATCH: "orange",
};

const isPlainObject = (value) =>
  Object.prototype.toString.call(value) === "[object Object]";

const formatDetailKey = (key = "") => {
  if (DETAIL_KEY_LABELS[key]) return DETAIL_KEY_LABELS[key];

  const cleaned = key.includes(".") ? key.split(".").pop() : key;
  return cleaned
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const parseAuditDetails = (rawDetails) => {
  if (!rawDetails) return null;

  if (typeof rawDetails === "string") {
    try {
      return JSON.parse(rawDetails);
    } catch {
      return { raw_details: rawDetails };
    }
  }

  return rawDetails;
};

const extractFlatDetails = (details, maxItems = 40) => {
  if (!isPlainObject(details)) return [];

  const items = [];
  const visit = (obj, parentKey = "", depth = 0) => {
    Object.entries(obj).forEach(([key, value]) => {
      if (value === undefined || items.length >= maxItems) return;

      const currentKey = parentKey ? `${parentKey}.${key}` : key;

      if (isPlainObject(value) && depth < 1) {
        visit(value, currentKey, depth + 1);
        return;
      }

      items.push({ key: currentKey, value });
    });
  };

  visit(details);
  return items;
};

const extractChangedFields = (details) => {
  if (!isPlainObject(details)) return [];

  const fields = new Set();
  const registerField = (value) => {
    if (typeof value === "string" && value.trim()) {
      fields.add(value.trim());
    }
  };

  [details.updated_fields, details.changed_fields, details.modified_fields]
    .filter(Array.isArray)
    .forEach((arr) => arr.forEach(registerField));

  Object.keys(details).forEach((key) => {
    if (key.startsWith("old_")) fields.add(key.replace(/^old_/, ""));
    if (key.startsWith("new_")) fields.add(key.replace(/^new_/, ""));
    if (key.startsWith("previous_")) fields.add(key.replace(/^previous_/, ""));
  });

  return Array.from(fields);
};

const extractBeforeAfterPairs = (details) => {
  if (!isPlainObject(details)) return [];

  const pairs = [];
  const seen = new Set();

  const oldValues = details.old_values || details.oldValues || details.before;
  const newValues = details.new_values || details.newValues || details.after;

  if (isPlainObject(oldValues) || isPlainObject(newValues)) {
    const keys = new Set([
      ...Object.keys(isPlainObject(oldValues) ? oldValues : {}),
      ...Object.keys(isPlainObject(newValues) ? newValues : {}),
    ]);

    keys.forEach((field) => {
      pairs.push({
        field,
        before: oldValues ? oldValues[field] : undefined,
        after: newValues ? newValues[field] : undefined,
      });
      seen.add(field);
    });
  }

  Object.entries(details).forEach(([key, beforeValue]) => {
    if (!key.startsWith("old_") && !key.startsWith("previous_")) return;

    const field = key.replace(/^old_/, "").replace(/^previous_/, "");
    if (!field || seen.has(field)) return;

    const afterValue = details[`new_${field}`] ?? details[field];
    pairs.push({ field, before: beforeValue, after: afterValue });
    seen.add(field);
  });

  return pairs;
};

const buildActionSummary = (log, details) => {
  if (!log) return null;

  const resourceText = log.resource_type
    ? `${log.resource_type}${log.resource_id ? ` #${log.resource_id}` : ""}`
    : log.resource_id
      ? `ressource #${log.resource_id}`
      : "ressource";

  const identifier = IDENTIFIER_KEYS.find(
    (key) => details && details[key] !== undefined && details[key] !== null
  );
  const identifierValue = identifier ? details[identifier] : null;
  const isIdentifierPrimitive =
    ["string", "number", "boolean"].includes(typeof identifierValue);
  const identifierText =
    identifier && isIdentifierPrimitive ? ` (${identifierValue})` : "";

  if (log.action_type === "CREATE") {
    return `Creation de ${resourceText}${identifierText}`;
  }
  if (log.action_type === "UPDATE") {
    return `Mise a jour de ${resourceText}${identifierText}`;
  }
  if (log.action_type === "DELETE") {
    return `Suppression de ${resourceText}${identifierText}`;
  }

  return null;
};

const AuditLogs = () => {
  const { token, user } = useAuth();
  const { selectedCompany } = useSelection();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isSuperAdmin =
    user?.is_superadmin === true || Number(user?.is_superadmin) === 1;

  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedLog, setSelectedLog] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [auditScope, setAuditScope] = useState("company");
  const effectiveAuditScope =
    isSuperAdmin && auditScope === "all" ? "all" : "company";
  const auditScopeKey =
    effectiveAuditScope === "all"
      ? "all"
      : `company:${selectedCompany || "none"}`;
  const latestAuditScopeKeyRef = useRef(auditScopeKey);
  useLayoutEffect(() => {
    latestAuditScopeKeyRef.current = auditScopeKey;
  }, [auditScopeKey]);
  const previousAuditScopeKeyRef = useRef(auditScopeKey);
  const requestGateRef = useRef(null);
  if (!requestGateRef.current) {
    requestGateRef.current = createAuditRequestGate();
  }

  const [filters, setFilters] = useState({
    userSearch: "",
    module: null,
    actionType: null,
    status: null,
    dateRange: null,
    ipAddress: null,
  });

  const moduleLabels = useMemo(
    () => ({
      produits: "Produits",
      ventes: "Ventes",
      achats: "Achats",
      rh: "Ressources humaines",
      depenses_commercial: "Depenses - Gestion commerciale",
      depenses_logistics: "Depenses - Logistique",
      depenses_hotellerie: "Depenses - Hotellerie",
      logistics: "Logistique",
      users: "Utilisateurs",
      utilisateurs: "Utilisateurs",
      companies: "Entreprises",
      entreprises: "Entreprises",
      ecommerce: "E-commerce",
      warehouse: "Établissements",
      stock: "Stock",
      payments: "Paiements",
      paiements: "Paiements",
      reports: "Rapports",
      settings: "Parametres",
    }),
    []
  );

  const availableModules = useMemo(
    () =>
      Object.keys(moduleLabels).map((value) => ({
        value,
        label: moduleLabels[value],
      })),
    [moduleLabels]
  );

  const actionTypes = ["CREATE", "UPDATE", "DELETE", "READ", "VIEW_HISTORY", "ARCHIVE", "EXPORT"];
  const statuses = ["success", "failure", "partial"];

  const getModuleLabel = (moduleKey) => {
    if (!moduleKey) return "N/A";
    return moduleLabels[moduleKey] || moduleKey;
  };

  const renderStatusTag = (status) => {
    const config = STATUS_CONFIG[status] || {
      icon: null,
      color: "default",
      label: status || "-",
    };

    return (
      <Tag icon={config.icon} color={config.color}>
        {config.label}
      </Tag>
    );
  };

  const renderMethodTag = (method) => {
    if (!method) return <Text type="secondary">-</Text>;
    return <Tag color={METHOD_COLORS[method] || "default"}>{method}</Tag>;
  };

  const getUserDisplay = (record) => {
    if (record.username) {
      return <Text strong>{record.username}</Text>;
    }
    if (record.user_id) {
      return <Text type="secondary">ID: {record.user_id} (supprime)</Text>;
    }
    return <Tag color="default">Systeme</Tag>;
  };

  const loadLogs = async (
    page = currentPage,
    size = pageSize,
    activeFilters = filters
  ) => {
    const requestScopeKey = auditScopeKey;
    const request = requestGateRef.current.start(requestScopeKey);
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: size.toString(),
        offset: ((page - 1) * size).toString(),
      });

      if (effectiveAuditScope === "all") {
        params.append("scope", "all");
      }

      if (activeFilters.userSearch?.trim()) {
        params.append("userSearch", activeFilters.userSearch.trim());
      }
      if (activeFilters.module) params.append("module", activeFilters.module);
      if (activeFilters.actionType) params.append("actionType", activeFilters.actionType);
      if (activeFilters.status) params.append("status", activeFilters.status);
      if (activeFilters.ipAddress) params.append("ipAddress", activeFilters.ipAddress);
      if (activeFilters.dateRange) {
        params.append("startDate", activeFilters.dateRange[0].format("YYYY-MM-DD"));
        params.append("endDate", activeFilters.dateRange[1].format("YYYY-MM-DD"));
      }

      const response = await axios.get(`/api/audit/logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: request.signal,
      });

      if (
        request.isCurrent(latestAuditScopeKeyRef.current) &&
        response.data.success
      ) {
        setLogs(response.data.logs || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      if (
        isAuditRequestCanceled(error, request.signal) ||
        !request.isCurrent(latestAuditScopeKeyRef.current)
      ) {
        return;
      }
      console.error("[AUDIT_LOGS] Erreur chargement:", error);
      message.error("Erreur lors du chargement des logs");
    } finally {
      const isCurrent = request.isCurrent(latestAuditScopeKeyRef.current);
      request.finish();
      if (isCurrent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const scopeChanged = previousAuditScopeKeyRef.current !== auditScopeKey;
    previousAuditScopeKeyRef.current = auditScopeKey;

    if (scopeChanged) {
      requestGateRef.current.cancel();
      setLogs([]);
      setTotal(0);
      setSelectedLog(null);
      setDrawerVisible(false);

      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }

    loadLogs(currentPage, pageSize, filters);
  }, [auditScopeKey, currentPage, pageSize, token]);

  useEffect(
    () => () => {
      requestGateRef.current?.cancel();
    },
    []
  );

  const applyFilters = () => {
    const firstPage = 1;
    if (shouldLoadAuditPageImmediately(currentPage)) {
      loadLogs(firstPage, pageSize, filters);
      return;
    }

    setCurrentPage(firstPage);
  };

  const resetFilters = () => {
    const resetValues = {
      userSearch: "",
      module: null,
      actionType: null,
      status: null,
      dateRange: null,
      ipAddress: null,
    };

    const firstPage = 1;
    setFilters(resetValues);
    if (shouldLoadAuditPageImmediately(currentPage)) {
      loadLogs(firstPage, pageSize, resetValues);
      return;
    }

    setCurrentPage(firstPage);
  };

  const showLogDetails = (record) => {
    setSelectedLog(record);
    setDrawerVisible(true);
  };

  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  };

  const parsedSelectedLogDetails = parseAuditDetails(selectedLog?.details);
  const selectedLogDetailEntries = extractFlatDetails(parsedSelectedLogDetails);
  const selectedLogChangedFields = extractChangedFields(parsedSelectedLogDetails);
  const selectedLogBeforeAfterPairs = extractBeforeAfterPairs(parsedSelectedLogDetails);
  const selectedLogActionSummary = buildActionSummary(
    selectedLog,
    parsedSelectedLogDetails
  );

  const renderDetailValue = (value) => {
    if (value === undefined || value === null || value === "") {
      return <Text type="secondary">N/A</Text>;
    }

    if (typeof value === "boolean") {
      return <Text>{value ? "Oui" : "Non"}</Text>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <Text type="secondary">[]</Text>;
      }

      const primitiveOnly = value.every(
        (item) =>
          item === null ||
          ["string", "number", "boolean"].includes(typeof item)
      );

      if (primitiveOnly) {
        return (
          <Space wrap>
            {value.map((item, index) => (
              <Tag key={`${String(item)}-${index}`}>{String(item)}</Tag>
            ))}
          </Space>
        );
      }

      return (
        <pre style={{ fontSize: 12, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    if (isPlainObject(value)) {
      return (
        <pre style={{ fontSize: 12, margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    return <Text>{String(value)}</Text>;
  };

  const columns = [
    {
      title: "Date/Heure",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      fixed: "left",
      render: (text) => (
        <Tooltip title={dayjs(text).format("DD/MM/YYYY HH:mm:ss")}>
          {dayjs(text).format("DD/MM HH:mm")}
        </Tooltip>
      ),
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
    },
    {
      title: "Utilisateur",
      dataIndex: "username",
      key: "username",
      width: 150,
      render: (_, record) => getUserDisplay(record),
    },
    {
      title: "Entreprise",
      dataIndex: "company_name",
      key: "company_name",
      width: 180,
      render: (text, record) => {
        if (text) return <Text>{text}</Text>;
        if (record.company_id) return <Text type="secondary">ID: {record.company_id}</Text>;
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: "Établissement",
      dataIndex: "warehouse_name",
      key: "warehouse_name",
      width: 180,
      render: (text, record) => {
        if (text) return <Text>{text}</Text>;
        if (record.warehouse_id) return <Text type="secondary">ID: {record.warehouse_id}</Text>;
        return <Text type="secondary">-</Text>;
      },
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      width: 140,
      render: (text) => {
        if (!text) return <Text type="secondary">-</Text>;
        return <Tag color="blue">{getModuleLabel(text)}</Tag>;
      },
      filters: availableModules.map((item) => ({ text: item.label, value: item.value })),
      onFilter: (value, record) => record.module === value,
    },
    {
      title: "Action",
      dataIndex: "action_type",
      key: "action_type",
      width: 120,
      render: (text) => (text ? <Tag color="purple">{text}</Tag> : <Text type="secondary">-</Text>),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (text) => renderStatusTag(text),
      filters: [
        { text: "Succes", value: "success" },
        { text: "Echec", value: "failure" },
        { text: "Partiel", value: "partial" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Ressource",
      dataIndex: "resource_type",
      key: "resource_type",
      width: 200,
      render: (text, record) => {
        if (!text) return <Text type="secondary">N/A</Text>;
        return (
          <Text>
            {text} <Text type="secondary">#{record.resource_id}</Text>
          </Text>
        );
      },
    },
    {
      title: "Methode",
      dataIndex: "method",
      key: "method",
      width: 90,
      render: (text) => renderMethodTag(text),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      fixed: "right",
      render: (_, record) => (
        <Button type="link" icon={<EyeOutlined />} onClick={() => showLogDetails(record)}>
          Details
        </Button>
      ),
    },
  ];

  const renderMobileLogCard = (record) => (
    <Card size="small" style={{ width: "100%", borderRadius: 14 }} bodyStyle={{ padding: 12 }}>
      <Space direction="vertical" size={10} style={{ width: "100%" }}>
        <Space align="start" style={{ width: "100%", justifyContent: "space-between" }}>
          <Text strong style={{ fontSize: 14 }}>
            {dayjs(record.created_at).format("DD/MM/YYYY HH:mm")}
          </Text>
          {renderStatusTag(record.status)}
        </Space>

        <Space wrap size={[6, 6]}>
          <Tag color="blue">{getModuleLabel(record.module)}</Tag>
          <Tag color="purple">{record.action_type || "-"}</Tag>
          {renderMethodTag(record.method)}
        </Space>

        <div style={{ display: "grid", gap: 6 }}>
          <Space size={4} align="start" wrap>
            <Text type="secondary">Utilisateur:</Text>
            {getUserDisplay(record)}
          </Space>
          <Text type="secondary">
            Entreprise: {record.company_name || (record.company_id ? `ID: ${record.company_id}` : "-")}
          </Text>
          <Text type="secondary">
            Établissement: {record.warehouse_name || (record.warehouse_id ? `ID: ${record.warehouse_id}` : "-")}
          </Text>
          <Text type="secondary">
            Ressource: {record.resource_type ? `${record.resource_type} #${record.resource_id || "N/A"}` : "N/A"}
          </Text>
        </div>

        <Button type="primary" icon={<EyeOutlined />} onClick={() => showLogDetails(record)} block>
          Voir details
        </Button>
      </Space>
    </Card>
  );

  const renderBeforeAfterDetails = () => {
    if (!(selectedLog?.action_type === "UPDATE" && selectedLogBeforeAfterPairs.length > 0)) {
      return null;
    }

    if (isMobile) {
      return (
        <Descriptions.Item label="Avant / Apres">
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            {selectedLogBeforeAfterPairs.map((pair) => (
              <Card key={pair.field} size="small" bodyStyle={{ padding: 10 }}>
                <Text strong>{formatDetailKey(pair.field)}</Text>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">Avant</Text>
                  <div style={{ marginTop: 4 }}>{renderDetailValue(pair.before)}</div>
                </div>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">Apres</Text>
                  <div style={{ marginTop: 4 }}>{renderDetailValue(pair.after)}</div>
                </div>
              </Card>
            ))}
          </Space>
        </Descriptions.Item>
      );
    }

    return (
      <Descriptions.Item label="Avant / Apres">
        <div
          style={{
            border: "1px solid #f0f0f0",
            borderRadius: 6,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "160px 1fr 1fr",
              gap: 8,
              padding: "8px 12px",
              background: "#fafafa",
              fontWeight: 600,
            }}
          >
            <Text strong>Champ</Text>
            <Text strong>Avant</Text>
            <Text strong>Apres</Text>
          </div>
          <div style={{ maxHeight: 220, overflow: "auto" }}>
            {selectedLogBeforeAfterPairs.map((pair) => (
              <div
                key={pair.field}
                style={{
                  display: "grid",
                  gridTemplateColumns: "160px 1fr 1fr",
                  gap: 8,
                  padding: "8px 12px",
                  borderTop: "1px solid #f0f0f0",
                  alignItems: "start",
                }}
              >
                <Text strong>{formatDetailKey(pair.field)}</Text>
                {renderDetailValue(pair.before)}
                {renderDetailValue(pair.after)}
              </div>
            ))}
          </div>
        </div>
      </Descriptions.Item>
    );
  };

  const renderActionDataDetails = () => {
    if (
      !ACTIONS_WITH_ENRICHED_DETAILS.has(selectedLog?.action_type) ||
      selectedLogDetailEntries.length === 0
    ) {
      return null;
    }

    return (
      <Descriptions.Item label="Donnees de l'action">
        <div
          style={{
            maxHeight: 260,
            overflow: "auto",
            border: "1px solid #f0f0f0",
            borderRadius: 6,
          }}
        >
          {selectedLogDetailEntries.map((entry) => (
            <div
              key={entry.key}
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "200px 1fr",
                gap: 8,
                padding: "8px 12px",
                borderTop: "1px solid #f5f5f5",
                alignItems: "start",
              }}
            >
              <Text strong>{formatDetailKey(entry.key)}</Text>
              {renderDetailValue(entry.value)}
            </div>
          ))}
        </div>
      </Descriptions.Item>
    );
  };

  return (
    <div
      style={{
        padding: isMobile ? "14px" : "24px",
        background: isMobile
          ? "linear-gradient(180deg, rgba(244, 248, 252, 0.95) 0%, rgba(255, 255, 255, 1) 18%)"
          : undefined,
      }}
    >
      <div style={{ marginBottom: 14 }}>
        <Title level={isMobile ? 3 : 2} style={{ marginBottom: 4 }}>
          Journal d'Audit
        </Title>
        <Text type="secondary">
          Consultation et recherche des logs d'activite systeme
        </Text>
      </div>

      <Card
        style={{ marginBottom: 14, borderRadius: isMobile ? 18 : undefined }}
        bodyStyle={isMobile ? { padding: 12 } : undefined}
      >
        <Space
          direction={isMobile ? "vertical" : "horizontal"}
          wrap={!isMobile}
          size={isMobile ? 10 : 8}
          style={{ width: "100%" }}
        >
          {isSuperAdmin && (
            <Select
              aria-label="Périmètre du journal d'audit"
              style={isMobile ? { width: "100%" } : { width: 220 }}
              value={auditScope}
              onChange={(value) => {
                setCurrentPage(1);
                setAuditScope(value);
              }}
            >
              <Option value="company">Entreprise sélectionnée</Option>
              <Option value="all">Toutes les entreprises</Option>
            </Select>
          )}

          <Input
            placeholder="Utilisateur (nom, email ou ID)"
            value={filters.userSearch}
            onChange={(e) => setFilters({ ...filters, userSearch: e.target.value })}
            style={isMobile ? { width: "100%" } : { width: 220 }}
            prefix={<SearchOutlined />}
          />

          <Select
            placeholder="Module"
            style={isMobile ? { width: "100%" } : { width: 150 }}
            value={filters.module}
            onChange={(val) => setFilters({ ...filters, module: val })}
            allowClear
          >
            {availableModules.map((m) => (
              <Option key={m.value} value={m.value}>
                {m.label}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Action"
            style={isMobile ? { width: "100%" } : { width: 120 }}
            value={filters.actionType}
            onChange={(val) => setFilters({ ...filters, actionType: val })}
            allowClear
          >
            {actionTypes.map((a) => (
              <Option key={a} value={a}>
                {a}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Statut"
            style={isMobile ? { width: "100%" } : { width: 130 }}
            value={filters.status}
            onChange={(val) => setFilters({ ...filters, status: val })}
            allowClear
          >
            {statuses.map((s) => (
              <Option key={s} value={s}>
                {s === "success" ? "Succes" : s === "failure" ? "Echec" : "Partiel"}
              </Option>
            ))}
          </Select>

          <RangePicker
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
            format="DD/MM/YYYY"
            placeholder={["Date debut", "Date fin"]}
            style={isMobile ? { width: "100%" } : { width: 250 }}
          />

          <Input
            placeholder="Adresse IP"
            value={filters.ipAddress}
            onChange={(e) => setFilters({ ...filters, ipAddress: e.target.value })}
            style={isMobile ? { width: "100%" } : { width: 160 }}
            prefix={<SearchOutlined />}
          />

          <Space
            direction={isMobile ? "vertical" : "horizontal"}
            style={isMobile ? { width: "100%" } : undefined}
          >
            <Button type="primary" icon={<FilterOutlined />} onClick={applyFilters} block={isMobile}>
              Appliquer
            </Button>
            <Button icon={<ReloadOutlined />} onClick={resetFilters} block={isMobile}>
              Reinitialiser
            </Button>
          </Space>
        </Space>
      </Card>

      {total > 0 && (
        <Alert
          message={`${total} logs trouves`}
          type="info"
          showIcon
          style={{ marginBottom: 14 }}
        />
      )}

      <Card bodyStyle={isMobile ? { padding: 10 } : undefined} style={{ borderRadius: isMobile ? 18 : undefined }}>
        {isMobile ? (
          <>
            <List
              loading={loading}
              dataSource={logs}
              split={false}
              locale={{ emptyText: "Aucun log trouve" }}
              renderItem={(record) => (
                <List.Item style={{ padding: "0 0 10px", border: "none" }} key={record.id}>
                  {renderMobileLogCard(record)}
                </List.Item>
              )}
            />

            {total > 0 && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: 6 }}>
                <Pagination
                  simple
                  current={currentPage}
                  pageSize={pageSize}
                  total={total}
                  onChange={handlePaginationChange}
                />
              </div>
            )}
          </>
        ) : (
          <Table
            columns={columns}
            dataSource={logs}
            rowKey="id"
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (value) => `Total ${value} logs`,
              onChange: handlePaginationChange,
            }}
            scroll={{ x: 1600, y: 600 }}
            size="small"
          />
        )}
      </Card>

      <Drawer
        title="Details du log"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        width={isMobile ? "100%" : 600}
        bodyStyle={isMobile ? { padding: 12 } : undefined}
      >
        {selectedLog && (
          <Descriptions column={1} bordered size={isMobile ? "middle" : "small"}>
            <Descriptions.Item label="ID">{selectedLog.id}</Descriptions.Item>
            <Descriptions.Item label="Date/Heure">
              {dayjs(selectedLog.created_at).format("DD/MM/YYYY HH:mm:ss")}
            </Descriptions.Item>
            <Descriptions.Item label="Utilisateur">{getUserDisplay(selectedLog)}</Descriptions.Item>
            <Descriptions.Item label="Module">
              <Tag color="blue">{getModuleLabel(selectedLog.module)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Action">
              <Tag color="purple">{selectedLog.action_type}</Tag>
            </Descriptions.Item>
            {selectedLogActionSummary &&
              ACTIONS_WITH_ENRICHED_DETAILS.has(selectedLog.action_type) && (
                <Descriptions.Item label="Resume action">
                  <Alert
                    showIcon
                    type={
                      selectedLog.action_type === "DELETE"
                        ? "warning"
                        : selectedLog.action_type === "UPDATE"
                          ? "info"
                          : "success"
                    }
                    message={selectedLogActionSummary}
                  />
                </Descriptions.Item>
              )}
            <Descriptions.Item label="Statut">{renderStatusTag(selectedLog.status)}</Descriptions.Item>
            <Descriptions.Item label="Methode HTTP">{selectedLog.method || "-"}</Descriptions.Item>
            <Descriptions.Item label="Type de ressource">{selectedLog.resource_type || "N/A"}</Descriptions.Item>
            <Descriptions.Item label="ID ressource">{selectedLog.resource_id || "N/A"}</Descriptions.Item>
            <Descriptions.Item label="Duree (ms)">{selectedLog.duration_ms || "N/A"}</Descriptions.Item>
            <Descriptions.Item label="Adresse IP">
              <Text code>{selectedLog.ip_address || "N/A"}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="User-Agent">
              <Text type="secondary" style={{ fontSize: 12, wordBreak: "break-word" }}>
                {selectedLog.user_agent || "N/A"}
              </Text>
            </Descriptions.Item>

            {selectedLog.action_type === "UPDATE" && selectedLogChangedFields.length > 0 && (
              <Descriptions.Item label="Champs impactes">
                <Space wrap>
                  {selectedLogChangedFields.map((field) => (
                    <Tag color="processing" key={field}>
                      {formatDetailKey(field)}
                    </Tag>
                  ))}
                </Space>
              </Descriptions.Item>
            )}

            {renderBeforeAfterDetails()}
            {renderActionDataDetails()}

            <Descriptions.Item label="Details JSON bruts">
              {parsedSelectedLogDetails ? (
                <pre
                  style={{
                    fontSize: 12,
                    maxHeight: 300,
                    overflow: "auto",
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {JSON.stringify(parsedSelectedLogDetails, null, 2)}
                </pre>
              ) : (
                <Text type="secondary">Aucun detail</Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
};

export default AuditLogs;
